/**
 * AN ARTICLE AS ONE LINE - a small picture of it, and its title linking to the live page on the
 * website in a new tab, so the cabinet stays open - wherever a list names the article something was
 * left on: the Inbox's comments and messages (the owner, 2026-10-20: "a link to the article on the
 * site for a quick jump, and a small picture, so the author knows which piece it is").
 *
 * A piece with no live page (a draft, one taken down) has a plain title, and one with no cover has
 * no picture: the server sends `webPath` and `coverUrl` only where there is one.
 */
import { useState } from 'react';
import type { ReactNode } from 'react';
import { ExternalLink } from '@moonx/ui';
import { WEBSITE_ORIGIN } from '../../auth/handoff/website-handoff';
import { useTranslation } from '../../../i18n';
import { useServedImageSrc } from '../../storage/emulator-image-url';
import classes from './article-line.module.css';

export interface ArticleLineProps {
  readonly title: string;
  readonly webPath?: string | undefined;
  readonly coverUrl?: string | undefined;
  /** A card's head: the title is the card's heading, and this id labels the card. */
  readonly headingId?: string;
  /** A quiet line under the title. */
  readonly children?: ReactNode;
}

export function ArticleLine({ title, webPath, coverUrl, headingId, children }: ArticleLineProps) {
  const { t } = useTranslation('cabinet');
  const name =
    webPath === undefined ? (
      title
    ) : (
      <a
        href={`${WEBSITE_ORIGIN}${webPath}`}
        target="_blank"
        rel="noopener"
        className={classes.link}
        title={t('articleLine.open')}
      >
        {title}
        <ExternalLink size={12} className={classes.away} aria-hidden="true" />
      </a>
    );
  return (
    <div className={headingId === undefined ? classes.line : `${classes.line} ${classes.head}`}>
      <Thumb url={coverUrl ?? null} />
      <div className={classes.words}>
        {headingId === undefined ? (
          <div className={classes.title}>{name}</div>
        ) : (
          <h2 id={headingId} className={classes.title}>
            {name}
          </h2>
        )}
        {children}
      </div>
    </div>
  );
}

/** Decoration (`alt=""`): the title beside it names the piece. Gone if no address serves it. */
function Thumb({ url }: { readonly url: string | null }) {
  const served = useServedImageSrc(url);
  const [broken, setBroken] = useState<string | null>(null);
  if (served.src === null || served.src === broken) {
    return null;
  }
  const src = served.src;
  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      className={classes.thumb}
      onError={() => {
        // The emulator's copy first, then the API's address once (`useServedImageSrc`), then none.
        if (src !== url) {
          served.onError();
        } else {
          setBroken(src);
        }
      }}
    />
  );
}
